<template>
  <div class="page">
    <sui-segment class="content" padded attached emphasis="secondary">
      <sui-container>
        <h2 class="ui header">
          <i class="window maximize grey icon" />
          <sui-header-content>Service {{service.name}}</sui-header-content>
        </h2>
        <sui-message warning icon="exclamation triangle" v-show="buttons.recreate">
          <sui-button icon="redo" label-position="left" color="orange" floated="right" @click="recreate()" data-goal="SERVICE_PAGE_WARNING_RECREATE">Redeploy service</sui-button>
          The service hasn't been deployed properly.<br>
          Try again.
        </sui-message>
        <sui-message icon="exclamation circle" dismissable error header="Error!" :content="error" v-show="error" @dismiss="error = null" />
        <sui-card-group class="state-cards doubling" :items-per-row="6">
          <ServiceProjectCard :service="service" />
          <ServiceImageCard :service="service" />
          <ServiceStatusCard :service="service" />
          <ServiceRemoteAccessCard :service="service" />
          <ServiceSourceCard :service="service" v-if="service.source && service.source.type" />
          <ServiceConfigurationCard :service="service" v-if="service.configuration" />
          <ServiceServicesCard :service="service" v-if="service.services[0]" />
          <ServiceContainersCard :service="service" />
        </sui-card-group>
        <sui-segment class="actions">
          <div style="float: right">
            <sui-button color="blue" icon="copy" label-position="left" :disabled="!access.serviceCreate" data-goal="SERVICE_PAGE_CLONE" v-show="buttons.clone" @click="clone()">Clone</sui-button>
            <sui-button-group color="red" floated="right">
              <sui-button class="icon labeled" icon="trash" :disabled="inProgress || !actions.serviceDestroy" data-goal="SERVICE_PAGE_DESTROY" v-if="buttons.destroy" @click="destroy()">Destroy</sui-button>
              <sui-dropdown class="icon" :class="{disabled: inProgress}" button icon="dropdown">
                <sui-dropdown-menu>
                  <sui-dropdown-item :disabled="!actions.serviceDestroy" data-goal="SERVICE_PAGE_DESTROY_FORCE" @click="destroy(true)">
                    Force destroy <b>{{service.name}}</b><br>
                    <small class="dropdown-item-description">Remove the service from D2C, ignore all errors</small>
                  </sui-dropdown-item>
                  <sui-dropdown-item data-goal="SERVICE_PAGE_DESTROY_DEPLOY" @click="destroyAndCreate()">
                    Deploy <b>{{service.name}}</b> from scratch<br>
                    <small class="dropdown-item-description">Destroy this service and deploy it again from scratch</small>
                  </sui-dropdown-item>
                </sui-dropdown-menu>
              </sui-dropdown>
            </sui-button-group>
          </div>
          <sui-button color="blue" icon="cog" label-position="left" :disabled="inProgress" @click="$modals.updateService.open(service)" v-show="buttons.update" data-goal="SERVICE_PAGE_EDIT">Edit</sui-button>
          <sui-button color="teal" icon="key" label-position="left" :disabled="!isDeployed || inProgress || !actions.serviceUpdate" v-show="buttons.resetPassword" @click="resetPassword()" data-goal="SERVICE_PAGE_PASSWORD">Reset password</sui-button>
        </sui-segment>
        <sui-tab class="tabs" :menu="{attached: 'top', color: 'blue', widths: tabsCount}">
          <sui-tab-pane class="basic" title="System logs" icon="file alternate outline" v-if="tabs.logs">
            <EntityLog :entity="service" />
          </sui-tab-pane>
          <sui-tab-pane class="basic" title="Cron tasks" icon="tasks" v-if="tabs.tasks">
            <CronTasks :entity="service" purpose="general" />
          </sui-tab-pane>
          <sui-tab-pane class="basic" title="Database backups" icon="save" v-if="tabs.backupDatabase">
            <CronTasks :entity="service" purpose="backup" />
          </sui-tab-pane>
          <sui-tab-pane class="basic" title="Volume backups" icon="save" v-if="tabs.backupVolume">
            <CronTasks :entity="service" purpose="volumeBackup" />
          </sui-tab-pane>
          <sui-tab-pane title="Webhooks" icon="share square" color="purple" v-if="tabs.webhooks">
            <Webhooks :service="service" />
          </sui-tab-pane>
          <sui-tab-pane title="Debug" icon="user secret" v-if="tabs.debug">
            <codemirror class="commands" v-model="serviceString" :options="{mode: 'javascript', lineNumbers: true, readOnly: true}" />
          </sui-tab-pane>
        </sui-tab>
        <br />
        <sui-segment basic>
          <ContainerCards :containers="containers" />
        </sui-segment>
      </sui-container>
    </sui-segment>
  </div>
</template>

<script>
  import cloneDeep from 'lodash/cloneDeep'
  import {mapGetters} from 'vuex'
  import ContainerCards from '@/components/cards/Containers'
  import CronTasks from '@/components/CronTasks'
  import ServiceConfigurationCard from '@/components/cards/ServiceConfiguration'
  import ServiceContainersCard from '@/components/cards/ServiceContainers'
  import ServiceImageCard from '@/components/cards/ServiceImage'
  import ServiceProjectCard from '@/components/cards/ServiceProject'
  import ServiceRemoteAccessCard from '@/components/cards/ServiceRemoteAccess'
  import ServiceServicesCard from '@/components/cards/ServiceServices'
  import ServiceSourceCard from '@/components/cards/ServiceSource'
  import ServiceStatusCard from '@/components/cards/ServiceStatus'
  import EntityLog from '@/components/EntityLog'
  import Webhooks from '@/components/Webhooks'
  import store from '@/store'
  import utils from '@/utils'

  export default {
    name: 'ServiceView',
    components: {
      ContainerCards,
      EntityLog,
      Webhooks,
      CronTasks,
      ServiceConfigurationCard,
      ServiceContainersCard,
      ServiceImageCard,
      ServiceProjectCard,
      ServiceRemoteAccessCard,
      ServiceServicesCard,
      ServiceSourceCard,
      ServiceStatusCard,
    },
    data() {
      return {
        error: null,
      }
    },
    computed: {
      ...mapGetters(['access', 'actionsById', 'config', 'services', 'providers']),
      actions() {
        return this.actionsById[this.service.id]
      },
      service() {
        const name = this.$route.params.service
        const service = this.services.find(entity => entity.id === name || entity.name === name)
        return service
      },
      serviceString() {
        return JSON.stringify(this.service, null, 2)
      },
      containers() {
        return this.service._containers
      },
      inProgress() {
        return this.service._inProgress
      },
      isDeployed() {
        return this.service._isDeployed
      },
      tabs() {
        const methods = this.service.constructor.methods
        return {
          logs: true,
          tasks: true,
          backupDatabase: methods.backupDatabase,
          backupVolume: methods.backupVolume,
          webhooks: methods.webhooks,
          debug: this.config.debug,
        }
      },
      tabsCount() {
        return Object.values(this.tabs).filter(tab => tab).length
      },
      buttons() {
        const methods = this.service.constructor.methods
        return {
          clone: methods.clone,
          destroy: methods.destroy,
          recreate: methods.recreate && !this.isDeployed && !this.inProgress,
          resetPassword: methods.resetPassword,
          update: methods.update,
        }
      },
    },
    sockets: {
      delete(data) {
        if (data.target === 'service' && data.id === this.service.id) {
          this.$router.push({path: '/error/not-exist', query: {service: this.service.name}})
        }
      },
    },
    methods: {
      clone() {
        this.$modals.createService.open({
          ...this.service._clone,
          id: null,
          containers: [],
          created: false,
          name: utils.generateName(),
        })
      },
      recreate() {
        this.error = null
        this.service.recreate().catch(error => {
          this.error = error.message
        })
      },
      destroyAndCreate() {
        return this.$modals.confirm.open({
          callback: () => {
            this.error = null
            this.service.recreate().catch(error => {
              this.error = error.message
            })
          }
        })
      },
      resetPassword() {
        const entity = cloneDeep(this.service)
        entity.upsert = entity.resetPassword
        this.$modals.updateService.open(entity, 'resetingPassword')
      },
      destroy(force = false) {
        return this.$modals.confirm.open({
          callback: () => {
            this.error = null
            utils.reachGoal('SERVICE_PAGE_DESTROY_TRY')
            this.service.destroy(force).then(() => {
              utils.reachGoal('SERVICE_PAGE_DESTROY_SUCCESS')
              this.$router.push(`/projects/${this.service._project.name}`)
            }).catch(error => {
              utils.reachGoal('SERVICE_PAGE_DESTROY_FAIL')
              this.error = error.message
            })
          },
        })
      },
    },
    beforeRouteEnter(to, from, next) {
      const name = to.params.service
      const service = store.getters.services.find(entity => {
        return entity.id === name || entity.name === name
      })
      if (service) {
        next()
      } else {
        next({path: '/error/not-exist', query: {service: name}})
      }
    },
  }
</script>

<style scoped>
  .page { display: flex; flex-direction: column; }
  .page > .content { flex: 1; }
  .actions { margin: 2em 0!important; }
  .tabs >>> > .menu > .item { font-size: 1.2em!important; }
  .tabs >>> .basic.segment { margin-top: -1px!important; background: #fff!important; }
  .basic.segment { padding: 0!important; border: none!important; }
</style>
